/**
 * @fileoverview The root component of the application.
 *
 * This file sets up the main application structure, including the router
 * and all the necessary context providers for state management (Theme,
 * Authentication, Language, Spinner settings, etc.).
 */

import { ThemeProvider } from './lib/ThemeContext';
import { SpinnerProvider } from './lib/SpinnerContext';
import { ParticipantsProvider } from './lib/ParticipantsContext';
import { SettingsProvider } from './lib/SettingsContext';
import { LanguageProvider } from './lib/LanguageContext';
import { GameProvider } from './lib/GameContext';
import { Frame } from './screens/Frame';
import { AuthProvider } from './context/AuthContext';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { SharedSpinner } from './components/SharedSpinner';
import { WheelProvider } from './lib/WheelContext';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useState } from 'react';

import { SidebarProvider } from './lib/SidebarContext';
import MonitorPage from './monitor/MonitorPage';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminRoute from './components/admin/AdminRoute';
import ImagePreloader from './components/ImagePreloader';

// Help pages
import HelpCenter from './pages/help/HelpCenter';
import HowToPlay from './pages/help/HowToPlay';
import About from './pages/help/About';
import PrivacyPolicy from './pages/help/PrivacyPolicy';
import TermsOfService from './pages/help/TermsOfService';
import Contact from './pages/help/Contact';
import Report from './pages/help/Report';
import Version from './pages/help/Version';
import FAQ from './pages/help/FAQ';
import CookieConsent from './components/CookieConsent';
import analyticsManager from './lib/analytics';
import SEOEnhancer from './components/SEOEnhancer';
import ConsentManager, { updateConsent } from './lib/gdpr-consent';


/**
 * The main App component.
 * It configures the BrowserRouter and nests all context providers to make
 * global state available throughout the application. It also defines the
 * top-level routes for the main application frame and shared spinners.
 * @returns {JSX.Element} The rendered application component.
 */
function App() {
  const [imagesLoaded, setImagesLoaded] = useState(false);

  return (
    <HelmetProvider>
      <BrowserRouter 
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true
      }}
      >
        <Helmet>
                      <title>Spinner Games Online - Free Kids & Family Games, Arcade Probability Fun</title>
          <meta name="description" content="🎯 Play free spinner games online! Fun spinner games for kids & families - Truth or Dare, Would You Rather, Trivia. Educational probability games people play. Best online spinner game arcade!" />
          <meta name="keywords" content="spinner game online, spinner games online, spinner games online free, spinner game for kids, spinner games for kids, spinner games arcade, spinner games people play, spinner games for probability, bottle spinner, spin the bottle, spin the wheel, truth or dare, would you rather, trivia games, quiz games, which one are you, decision maker, party games, family games, online spinner, bottle spin, quiz app, personality test, random selector, wheel of fortune, educational games, interactive games" />
          
          {/* Enhanced Open Graph Meta Tags */}
          <meta property="og:title" content="Spinner Games - Spin the Bottle, Truth or Dare, Would You Rather, Trivia Quiz Games" />
          <meta property="og:description" content="🎯 Free bottle spinner games with Truth or Dare, Would You Rather, Trivia quiz games, and Which One Are You personality tests. Interactive decision maker for parties and family fun!" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://spinnergames.fun" />
          <meta property="og:image" content="https://spinnergames.fun/social-share.png" />
          <meta property="og:site_name" content="Spinner Games !!" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:type" content="image/png" />
          <meta property="og:image:alt" content="Spinner Games - Truth or Dare, Would You Rather, Trivia Quiz Games, Spin the Bottle, Spin the wheel" />
          <meta property="og:image:secure_url" content="https://spinnergames.fun/social-share.png" />
          <meta property="og:locale" content="en_US" />
          
          {/* Enhanced Twitter Card Meta Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Spinner Games - Spin the Bottle, Truth or Dare, Would You Rather, Trivia Quiz Games" />
          <meta name="twitter:description" content="🎯 Free bottle spinner games with Truth or Dare, Would You Rather, Trivia quiz games, and Which One Are You personality tests. Interactive decision maker for parties!" />
          <meta name="twitter:image" content="https://spinnergames.fun/social-share.png" />
          <meta name="twitter:url" content="https://spinnergames.fun" />
          <meta name="twitter:site" content="@spinnergames" />
          
          {/* Enhanced Meta Tags for Better SEO */}
                      <meta name="title" content="Spinner Games - Spin the Bottle, Truth or Dare, Would You Rather, Trivia Quiz Games" />
          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          <meta name="author" content="Spinner Games !!" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          {/* Canonical tags are set per-page via Helmet in individual route components */}
          
          {/* Additional SEO Meta Tags */}
          <meta name="application-name" content="Spinner Games" />
          <meta name="apple-mobile-web-app-title" content="Spinner Games" />
          <meta name="theme-color" content="#0053d0" />
          <meta name="msapplication-TileColor" content="#0053d0" />
          <meta name="msapplication-config" content="/browserconfig.xml" />
          
          {/* Language and Region Meta Tags */}
          <meta name="language" content="English" />
          <meta name="geo.region" content="US" />
          <meta name="geo.placename" content="United States" />
          
          {/* Structured Data for Rich Snippets */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "Spinner Games Online - Free Kids & Family Spinner Game Arcade",
              "description": "Play free spinner games online! Fun spinner games for kids and families featuring Truth or Dare, Would You Rather, Trivia. Educational probability games people play in our online spinner game arcade.",
              "url": "https://spinnergames.fun",
              "applicationCategory": "GameApplication",
              "operatingSystem": "Web Browser",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "ratingCount": "1250"
              },
              "author": {
                "@type": "Organization",
                "name": "Spinner Games"
              },
              "featureList": [
                "Free Spinner Games Online",
                "Spinner Games for Kids",
                "Spinner Game Arcade",
                "Spinner Games for Probability",
                "Truth or Dare Spinner Games",
                "Would You Rather Games", 
                "Trivia Quiz Spinner Games",
                "Educational Games People Play",
                "Interactive Decision Maker",
                "Party & Family Games"
              ]
            })}
          </script>
          
          {/* Additional Structured Data for Games */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "name": "Spinner Games Collection",
              "description": "Collection of interactive spinner games including Truth or Dare, Would You Rather, Trivia, and personality tests",
              "itemListElement": [
                {
                  "@type": "Game",
                  "position": 1,
                  "name": "Bottle Spinner",
                  "description": "Interactive bottle spinner for decision making and random selection"
                },
                {
                  "@type": "Game", 
                  "position": 2,
                  "name": "Truth or Dare",
                  "description": "Family-friendly Truth or Dare game with curated questions"
                },
                {
                  "@type": "Game",
                  "position": 3, 
                  "name": "Would You Rather",
                  "description": "Decision-making game with challenging scenarios"
                },
                {
                  "@type": "Game",
                  "position": 4,
                  "name": "Trivia Quiz",
                  "description": "Educational trivia games covering various subjects"
                },
                {
                  "@type": "Game",
                  "position": 5,
                  "name": "Which One Are You",
                  "description": "Personality test game for self-discovery"
                }
              ]
            })}
          </script>
        </Helmet>
        

        
        <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <SettingsProvider>
              <ParticipantsProvider>
                <SpinnerProvider>
                  <WheelProvider>
                    <GameProvider>
                      <SidebarProvider>
                        {/* Image Preloader - loads all spinner and game card images */}
                        <ImagePreloader 
                          onComplete={() => setImagesLoaded(true)}
                          showProgress={!imagesLoaded}
                        />
                        
                        <Routes>
                          {/* Help routes */}
                          <Route path="/help" element={<HelpCenter />} />
                          <Route path="/help/how-to-play" element={<HowToPlay />} />
                          <Route path="/help/about" element={<About />} />
                          <Route path="/help/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/help/terms" element={<TermsOfService />} />
                          <Route path="/help/contact" element={<Contact />} />
                          <Route path="/help/report" element={<Report />} />
                          <Route path="/help/version" element={<Version />} />
                          <Route path="/help/faq" element={<FAQ />} />

                          
                          {/* Route for loading a spinner configuration from a shared link */}
                          <Route path="/share/:spinnerId" element={<SharedSpinner />} />

                          {/* Monitor route */}
                          <Route path="/monitor" element={<MonitorPage />} />
                          
                          {/* Admin routes */}
                          <Route path="/appadmin/login" element={<AdminLogin />} />
                          <Route path="/appadmin/dashboard" element={
                            <AdminRoute>
                              <AdminDashboard />
                            </AdminRoute>
                          } />
                          
                          {/* Default route that loads the main application interface */}
                          <Route path="*" element={<Frame />} />
                        </Routes>

                      </SidebarProvider>
                    </GameProvider>
                  </WheelProvider>
                </SpinnerProvider>
              </ParticipantsProvider>
            </SettingsProvider>
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
      
      {/* SEO Enhancement for Crawlers */}
      <SEOEnhancer />
      
      {/* GDPR Compliant Cookie Consent Banner */}
      <CookieConsent
        onAccept={() => {
          console.log('All cookies accepted');
          updateConsent({
            essential: true,
            analytics: true,
            advertising: true,
            functional: true
          });
        }}
        onDecline={() => {
          console.log('Non-essential cookies declined');
          updateConsent({
            essential: true,
            analytics: false,
            advertising: false,
            functional: false
          });
        }}
        onCustomize={() => {
          console.log('Cookie customization requested');
        }}
      />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
